// © Microsoft Corporation. All rights reserved.
import { __awaiter } from "tslib";
import { FailedToClearIdb, FailedToHydrateIdb, FailedToInitIdb, FailedToSaveIdb } from "../AppErrors";
import { IndexedDb } from "./IndexedDb";
import { logger } from "./Logger";
import { registerCommandMessageHandler } from "./MessageHandler";
/**
 * IndexedDBCache
 * Helper class that hydrates from IndexedDB and saves data to IndexedDB
 */
export class IndexedDBCache {
    constructor(options) {
        this.options = options;
        /** In Memory Cache for data from IndexedDB */
        this.cache = new Map();
        /** Flag indicating if IndexedDB is supported */
        this.isIndexedDBSupported = false;
        /** Stale items in cache */
        this.staleItems = [];
        options.dbVersion = options.dbVersion || 1;
        this.registerIndexedDBCommandHandler();
    }
    /**
     * Connects to an IndexedDB instance and hydrates in memory cache
     * @param idb indexedDb instance, only used for unit tests
     */
    initialize(idb = new IndexedDb()) {
        return __awaiter(this, void 0, void 0, function* () {
            this.idb = idb;
            this.isIndexedDBSupported = this.idb.supported;
            if (!this.isIndexedDBSupported) {
                return;
            }
            try {
                yield this.idb.initializeIndexedDb(this.options.dbVersion, this.options.storeName, this.options.dbName);
                yield this.hydrate();
            }
            catch (ex) {
                logger.error(Object.assign(Object.assign({}, FailedToInitIdb), { message: `Failed to init IndexedDB [${this.options.dbName}|${this.options.storeName}]`, pb: Object.assign(Object.assign({}, FailedToInitIdb.pb), { customMessage: `Error: ${ex}` }) }));
            }
        });
    }
    /**
     * Checks if data with given key is present in InMemoryCache
     * @param key the key to look up
     */
    has(key) {
        return this.cache.has(key);
    }
    /**
     * Gets an item from in-memory cache
     * @param key item key
     */
    get(key) {
        return this.cache.get(key);
    }
    /**
     * Returns the in memory cache entries as any Array.
     * Not returning a Map since it does not serialize/deserialize correctly in post messages
     * @param key item key
     */
    toArray() {
        // Array.from is a simpler way to do this, not doing it here since it's not supported in IE.
        const entries = [];
        this.cache.forEach(item => {
            entries.push(item);
        });
        return entries;
    }
    /**
     * Returns stale items in cache
     */
    getStaleItems() {
        return this.staleItems;
    }
    /**
     * Adds an item into in-memory cache
     * @param key item key
     */
    set(key, payload) {
        if (!payload || this.cache.size === this.options.maxItems) {
            return;
        }
        if (!payload.storageTimestamp) {
            payload.storageTimestamp = Date.now();
        }
        this.cache.set(key, payload);
    }
    /**
     * Saves the items in indexed db and in memory cache
     * @param key - optional key if only 1 entry is to be saved ti IDB else the entire in memory cache is saved
     */
    saveToIndexedDB() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.isIndexedDBSupported) {
                return;
            }
            try {
                // To avoid stale data in IndexedB, overwrite IndexedDB with new data even if it's empty
                yield this.idb.setObject(this.options.indexName, this.cache);
            }
            catch (ex) {
                logger.error(Object.assign(Object.assign({}, FailedToSaveIdb), { message: `Failed to save data in IndexedDB [${this.options.dbName}|${this.options.storeName}]`, pb: Object.assign(Object.assign({}, FailedToSaveIdb.pb), { customMessage: `Error: ${ex}` }) }));
            }
        });
    }
    /**
     * Clears the IndexedDB index
     * @param - Boolean to enable/disable local cache clear. By default, local cacheis cleared.
     */
    clearIndexedDB(clearLocalCache = true) {
        return __awaiter(this, void 0, void 0, function* () {
            if (clearLocalCache) {
                this.cache.clear();
            }
            if (!this.isIndexedDBSupported) {
                return;
            }
            try {
                yield this.idb.clearObjects();
            }
            catch (ex) {
                logger.error(Object.assign(Object.assign({}, FailedToClearIdb), { message: `Failed to clear cache [${this.options.dbName}|${this.options.storeName}]`, pb: Object.assign(Object.assign({}, FailedToClearIdb.pb), { customMessage: `Error: ${ex}` }) }));
            }
        });
    }
    /**
     * Returns the number of items in the cache
     */
    size() {
        return this.cache.size;
    }
    /**
     * Hydrates in-memory cache with items from IndexedDB
     */
    hydrate() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const indexedDBData = yield this.idb.getObject(this.options.indexName);
                if (!indexedDBData) {
                    return;
                }
                if (indexedDBData instanceof Map) {
                    this.populateInMemoryCache(indexedDBData);
                    return;
                }
                const dataConverter = this.options.convertToMap;
                if (dataConverter) {
                    const convertedData = dataConverter(indexedDBData);
                    if (!convertedData) {
                        return;
                    }
                    this.populateInMemoryCache(convertedData);
                }
            }
            catch (ex) {
                logger.error(Object.assign(Object.assign({}, FailedToHydrateIdb), { message: `Failed to hydrate from IndexedDB [${this.options.dbName}|${this.options.storeName}`, pb: Object.assign(Object.assign({}, FailedToHydrateIdb.pb), { customMessage: `${this.options.indexName}]. Error: ${ex}` }) }));
            }
        });
    }
    /**
     * Populates in memory cache with data from IndexedDB. Optionally filters stale items.
     * @param items - items to filter
     */
    populateInMemoryCache(items) {
        // Skip filtering if freshness duration is not set
        if (this.options.freshnessDuration === undefined) {
            items.forEach((item, key) => {
                this.set(key, item);
            });
            return;
        }
        const now = Date.now();
        items.forEach((item, key) => {
            if (!item.storageTimestamp) {
                return;
            }
            // Add to cache only if the item is not stale.
            if ((item.storageTimestamp + this.options.freshnessDuration) > now) {
                this.set(key, item);
            }
            else {
                this.staleItems.push(item);
            }
        });
    }
    registerIndexedDBCommandHandler() {
        const messageId = `IndexedDBCache-${this.options.dbName}-${this.options.storeName}`;
        registerCommandMessageHandler(messageId, (payload) => {
            if (!Array.isArray(payload)) {
                return;
            }
            let hasUpdates = false;
            payload.forEach((item) => {
                if (!item || !item.id) {
                    return;
                }
                const existingItem = this.cache.get(item.id);
                if (existingItem && existingItem.storageTimestamp > item.storageTimestamp) {
                    // What we have is newer, return
                    return;
                }
                this.set(item.id, item);
                hasUpdates = true;
            });
            if (hasUpdates) {
                this.saveToIndexedDB();
            }
        });
    }
}
//# sourceMappingURL=IndexedDBCache.js.map